import { forwardRef } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'accent' | 'ghost';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  block?: boolean;
};

/**
 * כפתור ראשי: מלבן פחם חד (radius-none), כמו ה CTA באתר.
 * כפתור משני: מסגרת ink-muted ו radius-sm. גובה 44 לפחות.
 * הטקסט בכפתור אומר מה יקרה.
 */
/**
 * לכל וריאנט יש מצב ריחוף ומצב לחיצה.
 *
 * בלעדיהם אי אפשר לדעת שמשהו הוא כפתור עד שלוחצים עליו, וזה נכון
 * במיוחד ל ghost: אייקון בלי רקע ובלי מסגרת נראה כמו קישוט. הרקע
 * שנדלק בריחוף הוא מה שמגלה את שטח הלחיצה.
 *
 * enabled: כדי שכפתור מושבת לא יענה לעכבר ויבטיח משהו שלא יקרה.
 */
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-charcoal text-on-charcoal rounded-none font-display font-semibold text-[20px] tracking-[0.01em] border border-charcoal enabled:hover:bg-ink enabled:hover:border-ink enabled:active:bg-ink',
  secondary:
    'border border-muted text-ink rounded-sm bg-transparent font-medium enabled:hover:border-ink enabled:hover:bg-cream enabled:active:bg-olive-soft',
  accent:
    'bg-olive text-on-olive border border-olive rounded-sm font-medium enabled:hover:bg-olive/85 enabled:hover:border-olive/85 enabled:active:bg-olive',
  ghost:
    'border border-transparent text-ink bg-transparent font-medium rounded-sm enabled:hover:bg-cream enabled:hover:border-line enabled:active:bg-olive-soft',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', block = false, className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 px-6 text-body leading-5',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-45',
        VARIANTS[variant],
        block && 'w-full',
        className,
      )}
      {...props}
    />
  );
});
